
import React, { useState, useMemo } from 'react';
import {
  Sparkles, Send, FileText, Loader2,
  LayoutTemplate, Check, Search, PenLine
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useEmailTemplates, useRenderEmailTemplate } from '@/hooks/email/useEmailTemplates';
import { useEmailSignatures } from '@/hooks/email/useEmailSignatures';
import { EmailSignature } from '@/services/api/emailSignatures';

interface EmailBottomToolbarProps {
  onSend: () => void;
  onSaveDraft: () => void;
  onClose: () => void;
  onAIGenerate?: () => void;
  isMaximized?: boolean;
  isSending?: boolean;
  isSavingDraft?: boolean;
  leadId?: string;
  contactId?: string;
  subject?: string;
  bodyHtml?: string;
  onSubjectChange?: (value: string) => void;
  onBodyHtmlChange?: (value: string) => void;
  onChange?: () => void;
  /** Append/replace a signature in the composed body (null = remove). */
  onSignatureSelect?: (signature: EmailSignature | null) => void;
  appliedSignatureId?: string | null;
  /** Template render resolved a recipient address (used when To is empty). */
  onRecipientResolved?: (email: string) => void;
}

const EmailBottomToolbar: React.FC<EmailBottomToolbarProps> = ({
  onSend,
  onSaveDraft,
  onClose,
  onAIGenerate,
  isMaximized = false,
  isSending = false,
  isSavingDraft = false,
  leadId,
  contactId,
  subject,
  bodyHtml,
  onSubjectChange,
  onBodyHtmlChange,
  onChange,
  onSignatureSelect,
  appliedSignatureId,
  onRecipientResolved,
}) => {
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateOpen, setTemplateOpen] = useState(false);

  const hasContext = Boolean(leadId || contactId);
  const { templates, isLoading: isLoadingTemplates } = useEmailTemplates({
    active: true,
    search: templateSearch,
  });
  const { renderTemplate, isRendering } = useRenderEmailTemplate();
  const { signatures, isLoading: isLoadingSignatures } = useEmailSignatures();

  const categories = useMemo(() => {
    const set = new Set<string>();
    templates.forEach((t) => {
      if (t.category) set.add(t.category);
    });
    return Array.from(set).sort();
  }, [templates]);

  const handleTemplateSelect = async (templateId: string) => {
    if (!templateId || !onSubjectChange || !onBodyHtmlChange || !onChange) return;
    try {
      const result = await renderTemplate({
        id: templateId,
        payload: {
          lead_id: leadId,
          contact_id: contactId,
        },
      });
      if (result?.subject) {
        onSubjectChange(result.subject);
      }
      if (result?.body_html) {
        onBodyHtmlChange(result.body_html);
      } else if (result?.body) {
        // Convert plain text to simple HTML if no HTML body returned
        const html = result.body
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n\n+/g, '</p><p>')
          .replace(/\n/g, '<br>');
        onBodyHtmlChange(`<p>${html}</p>`);
      }
      if (result?.recipient?.email) {
        onRecipientResolved?.(result.recipient.email);
      }
      onChange();
      setTemplateOpen(false);
    } catch {
      // Error toast handled by the mutation hook
    }
  };

  const isApplyingTemplate = isRendering;

  return (
    <>
      <div className="flex items-center justify-between p-2 border-t bg-background">
        {/* Left side - Primary action */}
        <div className="flex items-center space-x-2">
          <Button 
            onClick={onSend} 
            size="sm" 
            disabled={isSending}
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onSaveDraft} 
            className="border"
            disabled={isSavingDraft}
          >
            {isSavingDraft ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            {isMaximized && <span className="ml-2">Draft</span>}
          </Button>
          {onAIGenerate && (
            <Button variant="ghost" size="sm" onClick={onAIGenerate} className="border">
              <Sparkles className="h-4 w-4" />
              {isMaximized && <span className="ml-2">AI</span>}
            </Button>
          )}
          {hasContext && (
            <DropdownMenu open={templateOpen} onOpenChange={setTemplateOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="border"
                  disabled={isApplyingTemplate || isLoadingTemplates}
                  title="Insert template"
                >
                  {isApplyingTemplate ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LayoutTemplate className="h-4 w-4" />
                  )}
                  {isMaximized && <span className="ml-2">Template</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel>Email Templates</DropdownMenuLabel>
                <div className="px-2 pb-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      value={templateSearch}
                      onChange={(e) => setTemplateSearch(e.target.value)}
                      placeholder="Search templates..."
                      className="pl-7 h-8 text-xs"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                <DropdownMenuSeparator />
                {isLoadingTemplates ? (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
                    Loading...
                  </div>
                ) : templates.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    No templates found.
                  </div>
                ) : (
                  <>
                    {categories.map((category) => (
                      <React.Fragment key={category}>
                        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                          {category}
                        </DropdownMenuLabel>
                        {templates
                          .filter((t) => t.category === category)
                          .map((template) => (
                            <DropdownMenuItem
                              key={template.id}
                              onClick={() => handleTemplateSelect(template.id)}
                              className="cursor-pointer"
                            >
                              <div className="flex items-center justify-between w-full gap-2">
                                <span className="truncate">{template.name}</span>
                                <span className="flex items-center gap-1 shrink-0">
                                  {template.is_global && (
                                    <Badge variant="outline" className="text-[10px] px-1 py-0">Shared</Badge>
                                  )}
                                  {subject && template.subject === subject && (
                                    <Check className="h-3.5 w-3.5 text-primary" />
                                  )}
                                </span>
                              </div>
                            </DropdownMenuItem>
                          ))}
                      </React.Fragment>
                    ))}
                    {templates.some((t) => !t.category) && (
                      <>
                        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                          Uncategorized
                        </DropdownMenuLabel>
                        {templates
                          .filter((t) => !t.category)
                          .map((template) => (
                            <DropdownMenuItem
                              key={template.id}
                              onClick={() => handleTemplateSelect(template.id)}
                              className="cursor-pointer"
                            >
                              <div className="flex items-center justify-between w-full gap-2">
                                <span className="truncate">{template.name}</span>
                                <span className="flex items-center gap-1 shrink-0">
                                  {template.is_global && (
                                    <Badge variant="outline" className="text-[10px] px-1 py-0">Shared</Badge>
                                  )}
                                  {subject && template.subject === subject && (
                                    <Check className="h-3.5 w-3.5 text-primary" />
                                  )}
                                </span>
                              </div>
                            </DropdownMenuItem>
                          ))}
                      </>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {onSignatureSelect && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="border"
                  disabled={isLoadingSignatures}
                  title="Insert signature"
                >
                  {isLoadingSignatures ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <PenLine className="h-4 w-4" />
                  )}
                  {isMaximized && <span className="ml-2">Signature</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel>Signatures</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {signatures.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    No signatures yet.
                  </div>
                ) : (
                  signatures.map((sig) => (
                    <DropdownMenuItem
                      key={sig.id}
                      onClick={() => onSignatureSelect(sig)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center justify-between w-full gap-2">
                        <span className="truncate">{sig.name}</span>
                        <span className="flex items-center gap-1 shrink-0">
                          {sig.is_default && (
                            <Badge variant="secondary" className="text-[10px] px-1 py-0">Default</Badge>
                          )}
                          {appliedSignatureId === sig.id && (
                            <Check className="h-3.5 w-3.5 text-primary" />
                          )}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
                {appliedSignatureId && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onSignatureSelect(null)}
                      className="cursor-pointer text-muted-foreground"
                    >
                      Remove signature
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>


        {/* Right side - Empty for balance */}
        <div className="flex items-center space-x-1">
        </div>
      </div>
    </>
  );
};

export default EmailBottomToolbar;
